import { useEffect, useState } from 'react';
import baseapi, { API_BASE, useApiOutage } from '../../baseapi';
import { LiveFrame } from '../junctions/LiveFrame';

interface BoxCamera { id: string; name: string | null; state: string }

/**
 * Live video of a holding box: asks GET /api/holding/boxes/{id} which camera is on the box and shows
 * it. Says so plainly when the box has no camera yet or its camera is offline.
 */
export function BoxVideo({ boxId }: { boxId: string }) {
  const [cams, setCams] = useState<BoxCamera[] | null>(null);
  const [failed, setFailed] = useState(false);
  const outage = useApiOutage();     // true only after 2 minutes without an answer: a restart shows no message
  const [noPicture, setNoPicture] = useState(false);

  useEffect(() => {
    let alive = true;
    setCams(null); setFailed(false); setNoPicture(false);
    baseapi.get(`holding/boxes/${encodeURIComponent(boxId)}`)
      .then(r => { if (alive) setCams(r.data?.cameras || []); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [boxId]);

  const cam = cams?.[0];
  const online = cam && (cam.state === 'online' || cam.state === 'replay');
  const note = (title: string, text: string) => (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
      <div className="text-[12px] font-bold text-neutral-200">{title}</div>
      <div className="text-[10.5px] text-neutral-400 mt-0.5">{text}</div>
    </div>
  );

  return (
    <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black">
      {failed && outage ? note('Camera details could not be loaded', 'Check the connection and open the box again.')
        : cams === null ? note('Loading camera…', '')
        : !cam ? note('No camera on this box yet', 'Video appears here once a camera is assigned to the box.')
        : !online || noPicture ? note('Camera is offline', `${cam.id} is not sending a picture right now.`)
        : <LiveFrame src={`${API_BASE}cameras/${encodeURIComponent(cam.id)}/snapshot.jpg`} alt={cam.id}
            className="w-full h-full object-contain" onError={() => setNoPicture(true)} />}
      {cam && (
        <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/70 text-[10px] font-mono text-white flex items-center gap-1">
          <span className={`w-1.5 h-1.5 rounded-full ${online && !noPicture ? 'bg-emerald-400' : 'bg-rose-500'}`} /> {cam.id}
        </span>
      )}
    </div>
  );
}
